'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { authService } from '@/services/authService';
import { employeeService } from '@/services/employeeService';
import { loggingService, UserActivityType } from '@/services/loggingService';
import { errorHandlingService, ErrorType } from '@/services/errorHandlingService';
import { performanceService } from '@/services/performanceService';

export default function Login() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [storeCode, setStoreCode] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetStoreCode, setResetStoreCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  // Performance monitoring for page load
  useEffect(() => {
    const startTime = performance.now();
    
    // Track page load performance
    return () => {
      performanceService.trackRenderTime('LoginPage', startTime);
    };
  }, []);

  const handleLogout = async () => {
    // Use the central logout method for consistency
    await authService.logout();
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    // Start performance tracking
    const startTime = performance.now();

    try {
      // For global admin, force store code to be 'admin'
      const loginStoreCode = email === 'admin@tiptracker.com' ? 'admin' : storeCode.toUpperCase();

      loggingService.info('Attempting login', { 
        email, 
        storeCode: loginStoreCode 
      });
      
      // Make the login request
      const response = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          storeCode: loginStoreCode
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Check if user has multiple stores
      if (data.hasMultipleStores) {
        // Store auth data temporarily
        localStorage.setItem('temp-auth', JSON.stringify(data));
        
        // Redirect to store selection page
        window.location.href = '/select-store';
        return;
      }
      
      // Store auth data in both localStorage and cookies as per cursor rules
      const loginData = {
        ...data,
        // Ensure global admin flag is set for admin@tiptracker.com
        isGlobalAdmin: email === 'admin@tiptracker.com'
      };
      
      localStorage.setItem('auth', JSON.stringify(loginData));
      localStorage.setItem('userType', data.role);
      localStorage.setItem('storeCode', data.storeCode);
      localStorage.setItem('isAuthenticated', 'true');
      
      // Save to user-session for AuthContext
      localStorage.setItem('user-session', JSON.stringify(loginData));

      // Set cookie for middleware authentication - use path=/ to ensure it's available everywhere
      document.cookie = `auth=${JSON.stringify(loginData)}; path=/; max-age=${7 * 24 * 60 * 60}`;

      // Initialize services with store code
      await authService.initialize(data.storeCode);
      await employeeService.initialize({ storeCode: data.storeCode });
      
      // Save auth data to server-side cookie as well
      await fetch('/api/auth/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      // Log successful login
      loggingService.logUserActivity({
        type: UserActivityType.LOGIN,
        description: 'User logged in successfully',
        userId: loginData.id,
        storeCode: loginData.storeCode,
        metadata: {
          email: loginData.email,
          role: loginData.role,
          isGlobalAdmin: loginData.isGlobalAdmin
        }
      });

      // Set user context in Sentry
      loggingService.setUserContext(
        loginData.id,
        loginData.email,
        loginData.storeCode,
        loginData.role
      );

      // Show success message
      toast.success(`Welcome${data.role === 'admin' ? ', Admin' : ''}!`);
      
      // Track login performance
      performanceService.trackUserInteraction('login', startTime);
      
      // Redirect based on role - use window.location.href for reliable redirection
      if (data.role === 'admin' && email === 'admin@tiptracker.com') {
        loggingService.info('Redirecting to admin organizations page');
        window.location.href = '/admin/organizations';
      } else if (data.role === 'employee') {
        loggingService.info('Redirecting to employee dashboard');
        window.location.href = `/employee/dashboard?storeCode=${data.storeCode}`;
      } else {
        loggingService.info('Redirecting to manager dashboard');
        window.location.href = `/dashboard?storeCode=${data.storeCode}`;
      }
    } catch (error) {
      // Handle error with error service
      const appError = errorHandlingService.handleError(error, {
        email,
        storeCode,
        component: 'LoginPage'
      });
      
      setError(appError.message || 'Invalid credentials. Please try again.');
      
      // Show error to user
      errorHandlingService.showErrorToast(appError, 'Login failed');
      
      // Log the failed login attempt
      loggingService.logUserActivity({
        type: UserActivityType.LOGIN,
        description: 'Failed login attempt',
        userId: 'unknown',
        storeCode: storeCode || 'unknown',
        metadata: {
          email,
          errorMessage: appError.message
        }
      });
      
      // Track failed login performance
      performanceService.trackUserInteraction('login_failed', startTime);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    // Start performance tracking
    const startTime = performance.now();

    try {
      if (!resetEmail || !resetStoreCode) {
        setError('Email and store code are required');
        return;
      }

      loggingService.info('Attempting password reset', { 
        email: resetEmail, 
        storeCode: resetStoreCode 
      });

      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: resetEmail,
          storeCode: resetStoreCode.toUpperCase()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password reset failed');
      }

      // Log the password reset request
      loggingService.logUserActivity({
        type: UserActivityType.PASSWORD_CHANGE,
        description: 'Password reset requested',
        userId: 'unknown',
        storeCode: resetStoreCode.toUpperCase(),
        metadata: {
          email: resetEmail
        }
      });

      errorHandlingService.showSuccessToast('Password reset email sent. Please check your inbox.');
      setIsResetting(false);
      // Clear reset form fields
      setResetEmail('');
      setResetStoreCode('');
      
      // Track password reset performance
      performanceService.trackUserInteraction('password_reset_request', startTime);
    } catch (error) {
      // Handle error with error service
      const appError = errorHandlingService.handleError(error, {
        email: resetEmail,
        storeCode: resetStoreCode,
        component: 'LoginPage:ForgotPassword'
      });
      
      setError(appError.message || 'Failed to send password reset email. Please try again.');
      
      // Show error to user
      errorHandlingService.showErrorToast(appError, 'Password reset failed');
      
      // Track failed password reset performance
      performanceService.trackUserInteraction('password_reset_failed', startTime);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8" style={{ backgroundColor: '#2E5941' }}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          Welcome to Tip Tracker
        </h2>
        <p className="mt-2 text-center text-sm text-gray-200">
          Sign in to manage your tips
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="storeCode" className="block text-sm font-medium text-gray-700">
                Store Code
              </label>
              <Input
                id="storeCode"
                type="text"
                value={storeCode}
                onChange={(e) => setStoreCode(e.target.value.toUpperCase())}
                className="mt-1"
                required
                placeholder="Enter your store code"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
                required
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
                required
                placeholder="Enter your password"
              />
            </div>

            {error && (
              <div className="text-red-500 text-sm">
                {error}
              </div>
            )}

            <div>
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
              <button
                type="button"
                onClick={() => setIsResetting(true)}
                className="mt-2 text-sm text-blue-600 hover:text-blue-500 w-full text-center"
              >
                Forgot your password?
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  New to Tip Tracker?
                </span>
              </div>
            </div>

            <div className="mt-6 grid gap-3">
              <Link href="/register/organization" className="w-full">
                <Button variant="outline" className="w-full">
                  Register Your Organization
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {isResetting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-96">
            <h3 className="text-lg font-semibold mb-4">Reset Password</h3>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label htmlFor="resetStoreCode" className="block text-sm font-medium text-gray-700">
                  Store Code
                </label>
                <Input
                  id="resetStoreCode"
                  type="text"
                  value={resetStoreCode}
                  onChange={(e) => setResetStoreCode(e.target.value.toUpperCase())}
                  required
                  placeholder="Enter your store code"
                />
              </div>
              <div>
                <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <Input
                  id="resetEmail"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                />
              </div>
              {error && (
                <div className="text-red-500 text-sm">
                  {error}
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsResetting(false)}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Email'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 